import { useState } from 'react'
import type { KBSection } from '../data/sections'
import type { KBIconName } from './KBIcons'
import { KBIcon } from './KBIcons'

// Unicode-префиксы для <option> мобильного <select> — внутри <option>
// нельзя рендерить SVG, поэтому используем короткую букву в квадратных
// скобках, по смыслу совпадающую с иконкой раздела.
const SECTION_PREFIX: Record<KBIconName, string> = {
  dashboard: '[Д]',
  clients: '[К]',
  requests: '[З]',
  kp: '[КП]',
  table: '[Т]',
  settings: '[⚙]',
  queue: '[О]',
  excel: '[X]',
  quotes: '[Кв]',
  accounts: '[А]',
  rates: '[$]',
  catalog: '[Кт]',
  bell: '[🔔]',
  roles: '[Р]',
  mail: '[✉]',
  orders: '[Зк]',
  suppliers: '[П]',
  analytics: '[Ан]',
  planning: '[Пл]',
}

interface Props {
  sections: KBSection[]
  activeId: string
  onNavigate: (id: string) => void
  children: React.ReactNode
}

export function KBLayout({ sections, activeId, onNavigate, children }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = new Set<string>()
    for (const sec of sections) {
      if (sec.subsections.some((sub) => sub.id === activeId)) s.add(sec.id)
    }
    if (s.size === 0 && sections[0]) s.add(sections[0].id)
    return s
  })

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="flex gap-0 min-h-[calc(100vh-80px)]">
      <nav className="w-64 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 overflow-y-auto hidden lg:block">
        <div className="p-4 space-y-1">
          <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3 px-2">
            Разделы
          </div>
          {sections.map((sec) => (
            <div key={sec.id}>
              <button
                onClick={() => toggle(sec.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <span className="text-gray-500 dark:text-gray-400">
                  <KBIcon name={sec.icon} />
                </span>
                <span className="flex-1 text-left">{sec.title}</span>
                <span className="text-gray-400 text-xs">{expanded.has(sec.id) ? '▾' : '▸'}</span>
              </button>
              {expanded.has(sec.id) && (
                <div className="ml-7 mt-0.5 space-y-0.5">
                  {sec.subsections.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => onNavigate(sub.id)}
                      className={`w-full text-left px-2 py-1 text-xs rounded-md transition-colors ${
                        activeId === sub.id
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 font-medium'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {sub.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </nav>

      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="lg:hidden p-3 border-b border-gray-200 dark:border-gray-700">
          <select
            value={activeId}
            onChange={(e) => onNavigate(e.target.value)}
            className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            {sections.map((sec) =>
              sec.subsections.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {SECTION_PREFIX[sec.icon]} {sec.title} → {sub.title}
                </option>
              )),
            )}
          </select>
        </div>
        <div className="p-6 max-w-4xl">{children}</div>
      </div>
    </div>
  )
}
